import { ApiClient, ApiDpopClient, CsrfService } from '@shared/services';
import { API_LATENCY } from '@shared/constants';

import Constants from '@/utils/constants';
import {
	responseErrorHandler,
	apiClientResponseErrorHandler,
	attachBrowserFingerprint,
	attachDeviceFingerprint,
	attachTestMode,
} from '@/data/interceptors';
import {
	sendCustomAnalyticsEvent,
	sendNetworkMetricEvent,
} from '@/utils/AnalyticsUtils';
import TestModeService from '@/services/testMode.service';
import { REFRESH_TOKEN } from '@/constants';

import * as apiName from './apiEndpointNames';

const csrfService = new CsrfService({
	baseURL: Constants.API_WEB_BASE_URL,
	timeout: Constants.API_TIMEOUT,
	headers: Constants.HEADERS,
	csrfCookieKey: Constants.X_CSRF_COOKIE,
	csrfKey: Constants.X_CSRF_TOKEN,
});

class Api {
	constructor() {
		ApiClient.registerNetworkMetricsObserver({
			sendNetworkMetricEvent: (...data) => sendNetworkMetricEvent(...data),
		});

		/**
		 * @type { ApiClient }
		 */
		this.apiWebClient = new ApiClient({
			withCredentials: true,
			baseURL: Constants.API_WEB_BASE_URL,
			timeout: Constants.API_TIMEOUT,
			headers: Constants.HEADERS,
			refreshToken: this.refreshToken,
		});
		this.apiWebClientWithDeviceFingerprint = new ApiClient({
			withCredentials: true,
			baseURL: Constants.API_WEB_BASE_URL,
			timeout: Constants.API_TIMEOUT,
			headers: Constants.HEADERS,
			refreshToken: this.refreshToken,
		});
		this.apiWebClientDownload = new ApiClient({
			withCredentials: true,
			baseURL: Constants.API_WEB_BASE_URL,
			timeout: 0,
			headers: Constants.HEADERS,
			responseType: 'blob',
			refreshToken: this.refreshToken,
		});
		this.apiClient = new ApiClient({
			withCredentials: true,
			baseURL: Constants.API_BASE_URL,
			timeout: Constants.API_TIMEOUT,
			headers: Constants.HEADERS,
			refreshToken: this.refreshToken,
		});
		/**
		 * @type { ApiDpopClient }
		 */
		this.apiTestModeDPoPClient = new ApiDpopClient({
			withCredentials: false,
			baseURL: Constants.API_TEST_MODE_BASE_URL,
			timeout: Constants.API_TIMEOUT,
			headers: Constants.TEST_CLIENT_HEADERS,
			refreshToken: this.refreshTestModeToken,
			refreshStatusCodes: [412],
		});
		/**
		 * @type { ApiClient }
		 */
		this.apiTestModeDPoPClientDownload = new ApiDpopClient({
			withCredentials: false,
			baseURL: Constants.API_TEST_MODE_BASE_URL,
			timeout: 0,
			headers: Constants.TEST_CLIENT_HEADERS,
			responseType: 'blob',
			refreshToken: this.refreshTestModeToken,
			refreshStatusCodes: [412],
		});
		this.apiTestModeDPoPClient.addRequestInterceptors(attachTestMode);
		this.apiTestModeDPoPClientDownload.addRequestInterceptors(attachTestMode);

		this.apiWebClient.addRequestInterceptors(attachBrowserFingerprint);
		this.apiWebClient.addRequestInterceptors(csrfService.addCSRFtoReq);

		this.apiClient.addRequestInterceptors(attachBrowserFingerprint);
		this.apiClient.addRequestInterceptors(csrfService.addCSRFtoReq);

		this.apiWebClientDownload.addRequestInterceptors(attachBrowserFingerprint);
		this.apiWebClientDownload.addRequestInterceptors(csrfService.addCSRFtoReq);

		this.apiWebClientWithDeviceFingerprint.addRequestInterceptors(
			attachBrowserFingerprint,
		);
		this.apiWebClientWithDeviceFingerprint.addRequestInterceptors(
			attachDeviceFingerprint,
		);
		this.apiWebClientWithDeviceFingerprint.addRequestInterceptors(
			csrfService.addCSRFtoReq,
		);

		this.apiTestModeDPoPClient.addResponseInterceptors(
			(response) => response,
			responseErrorHandler,
		);
		this.apiTestModeDPoPClientDownload.addResponseInterceptors(
			(response) => response,
			responseErrorHandler,
		);
		this.apiWebClient.addResponseInterceptors(
			csrfService.checkCSRFinRes,
			responseErrorHandler,
		);
		this.apiClient.addResponseInterceptors(
			csrfService.checkCSRFinRes,
			apiClientResponseErrorHandler,
		);
		this.apiWebClientDownload.addResponseInterceptors(
			csrfService.checkCSRFinRes,
			responseErrorHandler,
		);
		this.apiWebClientWithDeviceFingerprint.addResponseInterceptors(
			csrfService.checkCSRFinRes,
			responseErrorHandler,
		);
	}

	get dynamicApiWebClient() {
		return TestModeService.isTestModeEnabled()
			? this.apiTestModeDPoPClient
			: this.apiWebClient;
	}

	get dynamicApiWebClientDownload() {
		return TestModeService.isTestModeEnabled()
			? this.apiTestModeDPoPClientDownload
			: this.apiWebClientDownload;
	}

	// Api declaration starts
	login = (data, captcha) =>
		this.apiWebClient.post(apiName.login.endPoint, {
			data,
			headers: {
				//Conditional captcha inclusion based on two login flows (SMS & EMAIL)
				...(captcha && { [Constants.HCAPTCHA_HEADER_KEY]: captcha }),
			},
		});

	otpLoginInitiate = (phoneNumber, captcha, channelType) =>
		this.apiWebClient.post(apiName.otpLoginInitiate.endPoint, {
			data: {
				type: 'OTP_V2',
				endpoint: phoneNumber,
				channelType,
				deviceFingerprint: Constants.DEVICE_FINGER_PRINT,
			},
			headers: {
				[Constants.HCAPTCHA_HEADER_KEY]: captcha,
			},
		});

	forgotPassword = (params, captcha) =>
		this.apiWebClient.post(apiName.forgotPassword.endPoint, {
			data: {},
			headers: {
				[Constants.HCAPTCHA_HEADER_KEY]: captcha,
			},
			params,
		});

	getRolesList = (params) =>
		this.apiWebClient.get(apiName.getRolesList.endPoint, {
			params: {
				admin: params ? params.admin : false,
			},
		});

	getUserPermissions = () =>
		this.apiWebClient.get(apiName.getUserPermissions.endPoint);

	getUserDetail = () => this.apiWebClient.get(apiName.getUserDetail.endPoint);

	logout = () => this.apiWebClient.post(apiName.logout.endPoint);

	getUsersList = (params) =>
		this.apiWebClient.get(apiName.getUsersList.endPoint, {
			params,
		});

	deleteUser = (data) => {
		const { userId } = data;
		return this.apiWebClient.delete(apiName.deleteUser.endPoint, {
			routeParams: { userId },
		});
	};

	createUser = (data, captchaToken) =>
		this.apiWebClient.post(apiName.createUser.endPoint, {
			data,
			headers: {
				[Constants.HCAPTCHA_HEADER_KEY]: captchaToken,
			},
		});

	assignUserRole = (data) =>
		this.apiWebClient.post(apiName.assignUserRole.endPoint, {
			routeParams: {
				userId: data.userId,
				roleId: data.roleId,
			},
		});

	deleteAssignedRole = (data) =>
		this.apiWebClient.put(apiName.deleteAssignedRole.endPoint, {
			routeParams: {
				userId: data.userId,
				roleId: data.roleId,
			},
		});
	getMerchantProfile = () =>
		this.apiWebClient.get(apiName.getMerchantProfile.endPoint);

	getPaymentOptions = () =>
		this.apiWebClient.get(apiName.getPaymentOptions.endPoint);

	setPassword = (data, captcha) =>
		this.apiWebClient.post(apiName.setPassword.endPoint, {
			data,
			headers: {
				[Constants.HCAPTCHA_HEADER_KEY]: captcha,
			},
		});

	// QRCode
	getQRList = (params) =>
		this.apiWebClient.get(apiName.getQRList.endPoint, {
			params,
		});

	getQRText = (id) =>
		this.apiWebClient.get(apiName.getQRText.endPoint, {
			routeParams: { id },
		});

	downloadQR = (merchantName, qrCodeId, qrUrl, storeName, terminalName) =>
		this.apiWebClientDownload.post(apiName.downloadQR.endPoint, {
			data: {
				merchantName,
				qrCodeId,
				qrUrl,
				storeName,
				terminalName,
			},
		});

	// Notification receiver
	getOTPNotificationReceiver = (qrCodeId, data) =>
		this.apiWebClient.post(apiName.getOTPNotificationReceiver.endPoint, {
			data,
			routeParams: { qrCodeId },
		});

	resendOTPNotificationReceiver = (data) =>
		this.apiWebClient.post(apiName.resendOTPNotificationReceiver.endPoint, {
			data,
		});

	verifyOTPNotificationReceiver = (qrCodeId, data) =>
		this.apiWebClient.post(apiName.verifyOTPNotificationReceiver.endPoint, {
			data,
			routeParams: {
				qrCodeId,
			},
		});

	deleteNotificationReceiver = (qrCodeId, data) =>
		this.apiWebClient.put(apiName.deleteNotificationReceiver.endPoint, {
			data,
			routeParams: {
				qrCodeId,
			},
		});

	// Notification receiver

	getLiveTransactionsV2 = (data) => {
		const cancelSource = ApiClient.getCancelController();
		const fetcher = this.apiWebClient.post(
			apiName.getLiveTransactionsV2.endPoint,
			{
				data,
				signal: cancelSource.signal,
			},
		);
		return { fetcher, cancel: cancelSource.cancel };
	};

	getTransactionListV3 = (data) => {
		const cancelSource = ApiClient.getCancelController();
		const response = this.dynamicApiWebClient
			.post(apiName.getTransactionListV3.endPoint, {
				data,
				signal: cancelSource.signal,
			})
			.then((res) => res.data);
		return { response, cancel: cancelSource.cancel };
	};

	getMerchantPaymentInstruments = (params) =>
		this.apiWebClient.get(apiName.getMerchantPaymentInstruments.endPoint, {
			params,
		});

	createPaylink = (data) =>
		this.apiWebClient.post(apiName.createPaylink.endPoint, {
			data,
		});

	resendPaylink = (data) =>
		this.apiWebClient.put(apiName.resendPaylink.endPoint, {
			routeParams: {
				data,
			},
		});

	cancelPaylink = (data) =>
		this.apiWebClient.put(apiName.cancelPaylink.endPoint, {
			routeParams: {
				data,
			},
		});

	getPaylinks = (data) =>
		this.apiWebClient.post(apiName.getPaylinks.endPoint, {
			data,
		});

	getTransactionDetail = (data) =>
		this.dynamicApiWebClient.post(apiName.getTransactionDetail.endPoint, {
			data,
		});

	getTransactionDetailByUnitId = (unitId, data) =>
		this.dynamicApiWebClient.get(
			apiName.getTransactionDetailByUnitId.endPoint,
			{
				routeParams: {
					unitId,
				},
				...data,
			},
		);

	refundTransaction = (data) =>
		this.apiWebClient.post(apiName.refundTransaction.endPoint, {
			data,
		});

	reportList = (params) =>
		this.dynamicApiWebClient.get(apiName.reportList.endPoint, {
			params,
		});

	getDailySettlements = (data) =>
		this.apiWebClient.post(apiName.getDailySettlements.endPoint, {
			data,
		});

	merchantList = (params) =>
		this.apiWebClient.get(apiName.merchantList.endPoint, {
			params,
		});

	downloadReport = (params) =>
		this.dynamicApiWebClientDownload.get(apiName.downloadReport.endPoint, {
			params: { selectedMerchant: params.selectedMerchant },
			routeParams: {
				id: params.id,
			},
		});

	getMonthlySettlement = (data) =>
		this.apiWebClient.post(apiName.getMonthlySettlement.endPoint, {
			data,
		});

	getGstInvoice = (data) =>
		this.apiWebClient.post(apiName.getGstInvoice.endPoint, {
			data,
		});

	getRecentSettlements = (data) =>
		this.apiWebClient.post(apiName.getRecentSettlements.endPoint, {
			data,
		});

	downloadRecentSettlementReport = (data) =>
		this.apiWebClientDownload.get(
			apiName.downloadRecentSettlementReport.endPoint,
			{
				routeParams: {
					data,
				},
			},
		);

	trends = (data) => this.apiWebClient.post(apiName.trends.endPoint, { data });

	getBatchRefunds = (params) =>
		this.apiWebClient.get(apiName.getBatchRefunds.endPoint, {
			params,
		});

	downloadBatchFile = (batchId) =>
		this.apiWebClientDownload.get(apiName.downloadBatchFile.endPoint, {
			routeParams: {
				batchId,
			},
		});

	downloadBulkRefundTemplate = () =>
		this.apiWebClientDownload.get(apiName.downloadBulkRefundTemplate.endPoint);

	uploadBatchFile = (data) => {
		const formData = new FormData();
		if (data.batchProcessorType)
			formData.append('batchProcessorType', data.batchProcessorType);
		if (data.description) formData.append('description', data.description);
		if (data.comment) formData.append('comment', data.comment);
		if (data.file) formData.append('file', data.file);

		const config = {
			headers: {
				'content-type': 'multipart/form-data',
			},
		};
		return this.apiWebClient.post(apiName.uploadBatchFile.endPoint, {
			data,
			headers: config.headers,
		});
	};

	generateReport = (data) =>
		this.apiWebClient.post(apiName.generateReport.endPoint, {
			data,
		});

	getMerchantHashId = (merchantId) =>
		this.apiWebClient.post(apiName.getMerchantHashId.endPoint, {
			routeParams: {
				merchantId,
			},
		});

	getStoreDetail = ({
		merchantId,
		storeId,
		includeImages = true,
		includeAddress = true,
	}) =>
		this.apiWebClient.get(apiName.getStoreDetail.endPoint, {
			params: { includeImages, includeAddress },
			routeParams: {
				merchantId,
				storeId,
			},
		});

	updateStore = (merchantId, storeId, data) =>
		this.apiWebClient.patch(apiName.updateStore.endPoint, {
			data,
			routeParams: {
				merchantId,
				storeId,
			},
		});

	getAddressFromLocation = (latitude, longitude) =>
		this.apiWebClient.get(apiName.getAddressFromLocation.endPoint, {
			params: {
				latitude,
				longitude,
			},
		});

	getAddressFromPinCode = (pincode) =>
		this.apiWebClient.get(apiName.getAddressFromPinCode.endPoint, {
			params: {
				address: pincode,
			},
		});

	addStoreImage = (data, merchantId, storeId, orderNo, stage, imageType) => {
		const formData = new FormData();
		if (merchantId) formData.append('merchantId', merchantId);
		if (storeId) formData.append('storeId', storeId);
		if (orderNo) formData.append('order_no', orderNo);
		if (stage) formData.append('stage', stage);
		if (imageType) formData.append('type', imageType);
		if (data) formData.append('file', data);

		const config = {
			headers: {
				'content-type': 'multipart/form-data',
			},
		};
		return this.apiWebClient.post(apiName.addStoreImage.endPoint, {
			data: formData,
			headers: config.headers,
		});
	};

	deleteStoreImage = (data) =>
		this.apiWebClient.delete(apiName.deleteStoreImage.endPoint, {
			params: { merchantId: data.merchantId, storeId: data.storeId },
			routeParams: {
				imageId: data.imageId,
			},
		});

	getStorePhoneUpdateOtp = (phoneNumber) =>
		this.apiWebClientWithDeviceFingerprint.post(
			apiName.getStorePhoneUpdateOtp.endPoint,
			{
				data: {
					phoneNumber,
				},
			},
		);

	verifyStorePhoneUpdateOtp = (data) =>
		this.apiWebClientWithDeviceFingerprint.post(
			apiName.verifyStorePhoneUpdateOtp.endPoint,
			{
				data: {
					token: data.token,
					otp: data.otp,
					phoneNumber: data.phoneNumber,
					merchantId: data.merchantId,
					storeId: data.storeId,
				},
			},
		);

	getHolidayList = () => this.apiWebClient.get(apiName.getHolidayList.endPoint);

	getTerminalList = (data) =>
		this.apiWebClient.get(apiName.getTerminalList.endPoint, {
			params: {
				skipInactive: data.skipInactive,
				start: data.start,
				limit: data.limit,
			},
			routeParams: {
				merchantId: data.merchantId,
				storeId: data.storeId,
			},
		});

	getDailyReportData = (data) =>
		this.dynamicApiWebClient.post(apiName.getDailyReportData.endPoint, {
			data,
		});

	// user profile update

	changeUserData = (userId, data) =>
		this.apiWebClient.put(apiName.changeUserData.endPoint, {
			data,
			routeParams: {
				userId,
			},
		});

	updateMerchantEmail = (merchantId, data, headers = {}) =>
		this.apiWebClient.put(apiName.updateMerchantEmail.endPoint, {
			data,
			headers,
			routeParams: { merchantId },
		});

	verifyHurdleOTP = (data) =>
		this.apiWebClient.post(apiName.verifyHurdleOTP.endPoint, {
			data,
			routeParams: {
				instanceId: data.instanceId,
			},
		});

	resendOTPForEmailUpdate = (data) =>
		this.apiWebClient.post(apiName.resendOTPForEmailUpdate.endPoint, { data });

	verifyUser = (verificationKey) =>
		this.apiWebClient.get(apiName.verifyUser.endPoint, {
			routeParams: {
				verificationKey,
			},
		});

	updateMerchantInfo = (data) =>
		this.apiWebClient.patch(apiName.updateMerchantInfo.endPoint, {
			data,
		});

	updateMerchantAddress = (data) =>
		this.apiWebClient.patch(apiName.updateMerchantAddress.endPoint, {
			data,
		});

	getGroupSelectionList = () =>
		this.apiWebClient.get(apiName.getGroupSelectionList.endPoint);

	updateUserGroup = (groupId) =>
		this.apiWebClient.post(apiName.updateUserGroup.endPoint, {
			data: {
				userGroupId: groupId,
			},
		});

	getUserLinkedStoreAccess = (data) =>
		this.apiWebClient.post(apiName.getUserLinkedStoreAccess.endPoint, {
			data,
		});

	getStoreList = (params) =>
		this.apiWebClient.get(apiName.getStoreList.endPoint, {
			params,
		});

	addStoreAccessToUser = (data) =>
		this.apiWebClient.post(apiName.addStoreAccessToUser.endPoint, {
			data: {
				userGroupId: data.userGroupId,
				entityName: 'STORE',
				entityValue: data.entityValue,
			},
			routeParams: {
				userId: data.userId,
			},
		});

	removeStoreAccessToUser = (data) =>
		this.apiWebClient.put(apiName.removeStoreAccessToUser.endPoint, {
			data: {
				userGroupId: data.userGroupId,
				entityName: 'STORE',
				entityValue: data.entityValue,
			},
			routeParams: { userId: data.userId },
		});

	getChatBotToken = () =>
		this.apiWebClient.get(apiName.getChatBotToken.endPoint);

	refreshToken = () =>
		this.apiWebClient
			.post(apiName.refreshToken.endPoint, {
				data: {},

				isSequential: false,
			})
			.then(() =>
				sendCustomAnalyticsEvent('custom', {
					name: REFRESH_TOKEN,
				}),
			);

	refreshTestModeToken = async () => {
		try {
			const response = await this.apiTestModeDPoPClient.post(
				apiName.refreshTestModeToken.endPoint,
				{
					data: {
						authToken: TestModeService.getTestModeToken(),
						refreshToken: TestModeService.getTestModeRefreshToken(),
					},
				},
			);
			return TestModeService.resetTokens(
				response.data.token,
				response.data.refreshToken,
			);
		} catch (error) {
			console.error('Error refreshing test mode token:', error);
			if (error.status === 500) {
				// If refresh also fails then we start the test mode again
				TestModeService.startTestMode(true);
			}
		}
	};

	removeAuthCookies = () =>
		this.apiWebClient.get(apiName.removeAuthCookies.endPoint, {});

	getMerchantOnboardingDetails = () =>
		this.apiWebClient.get(apiName.getMerchantOnboardingDetails.endPoint);

	getChimeraConfig = (data) =>
		this.apiWebClient.post(apiName.getChimeraConfig.endPoint, {
			data,
			routeParams: {
				merchantId: data.merchantId,
			},
		});

	getSettlementReportStatus = (params) =>
		this.apiWebClient.get(apiName.getSettlementReportStatus.endPoint, {
			params: { settlementDate: params.settlementDate },
			routeParams: {
				merchantId: params.merchantId,
				urn: params.urn,
			},
		});

	getMonthlySettlementReport = (params) =>
		this.apiWebClient.get(apiName.getMonthlySettlementReport.endPoint, {
			params: { ...params },
		});

	getSettlementDefaultReportTemplate = (params) =>
		this.apiWebClient.get(apiName.getDefaultReportTemplate.endPoint, {
			params,
		});

	getSettlementReportTemplates = (params) =>
		this.apiWebClient.get(apiName.getSettlementReportTemplates.endPoint, {
			params,
		});

	updateDefaultReportTemplate = (data) =>
		this.apiWebClient.put(apiName.updateDefaultReportTemplate.endPoint, {
			data,
		});

	generateSettlementReport = (data) =>
		this.apiWebClient.post(apiName.generateSettlementReport.endPoint, {
			data,
		});

	bullhornRequestTopics = (data, merchantUserId) => {
		return this.apiWebClient.post(apiName.bullhornRequestTopics.endPoint, {
			data,
			routeParams: {
				merchantUserId,
			},
		});
	};
	bullhornRequestSync = (data, merchantUserId) => {
		return this.apiWebClient.post(apiName.bullhornRequestSync.endPoint, {
			data,
			routeParams: {
				merchantUserId,
			},
		});
	};
	bullhornRequestRestore = (data, merchantUserId) => {
		return this.apiWebClient.post(apiName.bullhornRequestRestore.endPoint, {
			data,
			routeParams: {
				merchantUserId,
			},
		});
	};
	generateCustomSettlementReport = (data) =>
		this.apiWebClient.post(apiName.generateCustomSettlementReport.endPoint, {
			data,
		});

	getReportEmailList = () => {
		return this.apiWebClient.get(apiName.getReportEmailList.endPoint);
	};

	fetchIrisAccessToken = () =>
		this.apiWebClient.post(apiName.fetchIrisAccessToken.endPoint);

	getApiKeysList = (data) =>
		this.apiWebClient.post(apiName.getApiKeysList.endPoint, {
			data,
		});

	getEncryptedApiKeys = (data) =>
		this.apiWebClient.post(apiName.getEncryptedApiKeys.endPoint, {
			data,
		});

	updateMerchantConfig = (data) =>
		this.apiWebClient.post(apiName.updateMerchantConfig.endPoint, { data });

	getDashboardStartupConfigDetails = () =>
		this.apiWebClient.get(apiName.getDashboardStartupConfigDetails.endPoint);

	sendFoxtrotEvents = (data) =>
		this.apiClient.post(apiName.sendFoxtrotEvents.endPoint, {
			data,
			skipEvent: [API_LATENCY],
		});

	omReportList = (params) =>
		this.apiWebClient.get(apiName.omReportList.endPoint, {
			params,
		});

	omGenerateReport = (data) =>
		this.dynamicApiWebClient.post(apiName.omGenerateReport.endPoint, {
			data,
		});

	omDownloadReport = (params) =>
		this.apiWebClientDownload.get(apiName.omDownloadReport.endPoint, {
			params: { selectedMerchant: params.selectedMerchant },
			routeParams: {
				id: params.id,
			},
		});

	initServiceLineForB2B = (data) =>
		this.apiWebClient.post(apiName.initServiceLineForB2B.endPoint, {
			data,
		});

	getServiceLineDetails = (serviceLine) =>
		this.apiWebClient.get(apiName.getServiceLineDetails.endPoint, {
			routeParams: {
				serviceLine,
			},
		});

	getServiceLinesList = (merchantId) =>
		this.apiWebClient.get(apiName.getServiceLinesList.endPoint, {
			routeParams: {
				merchantId,
			},
		});

	getBusinessSuperCategoryList = () =>
		this.apiWebClient.get(apiName.getBusinessSuperCategoryList.endPoint);

	// Same API call is used for category and sub category list
	getBusinessCategoryList = (categoryNodeId) =>
		this.apiWebClient.get(apiName.getBusinessCategoryList.endPoint, {
			routeParams: {
				categoryNodeId,
			},
		});

	getPincodeDetails = (pincode) =>
		this.apiWebClient.get(apiName.getPincodeDetails.endPoint, {
			routeParams: {
				pincode,
			},
		});

	updateMerchantDetails = (data) =>
		this.apiWebClient.patch(apiName.updateMerchantDetails.endPoint, {
			data,
		});

	kycInitV2 = (data) =>
		this.apiWebClient.post(apiName.kycInitV2.endPoint, { data });

	sentEmailVerification = (data) =>
		this.apiWebClient.post(apiName.sentEmailVerification.endPoint, { data });

	getWebhookEvents = () =>
		this.dynamicApiWebClient.get(apiName.getWebhookEvents.endPoint);

	getWebhooksList = ({ limit, offset }) =>
		this.dynamicApiWebClient.get(apiName.getWebhooksList.endPoint, {
			params: { limit, offset },
		});

	createWebhook = (data) =>
		this.dynamicApiWebClient.post(apiName.createWebhook.endPoint, {
			data,
		});

	editWebhook = ({ webhookId, data }) =>
		this.dynamicApiWebClient.put(apiName.editWebhook.endPoint, {
			data,
			routeParams: {
				webhookId,
			},
		});

	deleteWebhook = (webhookId) =>
		this.dynamicApiWebClient.delete(apiName.deleteWebhook.endPoint, {
			routeParams: {
				webhookId,
			},
		});

	sendOtp = (data, captcha) => {
		return this.apiWebClient.post(apiName.sendOtp.endPoint, {
			data,
			headers: {
				[Constants.HCAPTCHA_HEADER_KEY]: captcha,
			},
		});
	};

	submitOtp = (data) =>
		this.apiWebClient.post(apiName.submitOtp.endPoint, {
			data,
		});

	initConsent = (data) =>
		this.apiWebClient.post(apiName.initConsent.endPoint, {
			data,
		});

	fetchPoliciesPreview = (data) =>
		this.apiWebClient.post(apiName.fetchPoliciesPreview.endPoint, {
			data,
		});

	downloadPolicies = (data) =>
		this.apiWebClientDownload.post(apiName.downloadPolicies.endPoint, {
			data,
		});

	initiateMira = (merchantId, data) =>
		this.apiWebClient.post(apiName.initiateMira.endPoint, {
			data,
			routeParams: {
				merchantId,
			},
		});

	getWebsiteType = (url, websiteType) =>
		this.apiWebClient.get(apiName.getWebsiteType.endPoint, {
			params: {
				url,
				websiteType,
			},
		});

	getMiraReport = (merchantId) =>
		this.apiWebClient.get(apiName.getMiraReport.endPoint, {
			routeParams: {
				merchantId,
			},
		});

	geMerchantJourneyStatus = (data) =>
		this.apiWebClient.post(apiName.geMerchantJourneyStatus.endPoint, {
			data,
		});

	/**
	 * @param {referralCode} - string
	 * @returns {Promise} - A promise that resolves with the masked details
	 */
	fetchMaskedDetails = (referralCode) =>
		this.apiWebClient.get(apiName.fetchMaskedDetails.endPoint, {
			routeParams: { referralCode },
		});

	/**
	 * @param {ValidationData} data - {phoneNumber: string, referralCode: string}
	 * @returns {Promise} - A promise that resolves with the validation result
	 */
	validateMaskedDetails = (data) =>
		this.apiWebClient.post(apiName.validateMaskedDetails.endPoint, {
			data,
		});

	verifyMerchantLoginRedirection = (data) =>
		this.apiWebClient.post(apiName.verifyMerchantLoginRedirection.endPoint, {
			data,
		});

	// Online Payment links
	createOnlinePaylink = (data) =>
		this.dynamicApiWebClient.post(apiName.createOnlinePaylink.endPoint, {
			data,
		});
	getOnlinePaylinks = (data) =>
		this.dynamicApiWebClient.post(apiName.getOnlinePaylinks.endPoint, {
			data,
		});
	cancelOnlinePaylink = (data) =>
		this.dynamicApiWebClient.post(apiName.cancelOnlinePaylink.endPoint, {
			data,
		});
	resendOnlinePaylink = (data) =>
		this.dynamicApiWebClient.post(apiName.resendOnlinePaylink.endPoint, {
			data,
		});

	getOnlineTransactionList = (data) => {
		const cancelSource = ApiClient.getCancelController();
		const response = this.dynamicApiWebClient
			.post(apiName.getOnlineTransactionList.endPoint, {
				data,

				signal: cancelSource.signal,
			})
			.then((res) => res.data);
		return { response, cancel: cancelSource.cancel };
	};
	getTransactionListNonAbort = (data) =>
		this.apiWebClient.post(apiName.getTransactionListNonAbort.endPoint, {
			data,
		});
	getOnlineTransactionDetailByUnitId = (transactionId) =>
		this.dynamicApiWebClient.get(
			apiName.getOnlineTransactionDetailByUnitId.endPoint,
			{
				routeParams: {
					transactionId,
				},
			},
		);

	getMerchantAccountDetails = ({ merchantId }) =>
		this.apiWebClient.get(apiName.getMerchantAccountDetails.endPoint, {
			routeParams: { merchantId },
		});

	getBankDetailsUsingIfsc = ({ ifsc }) =>
		this.apiWebClient.get(apiName.getBankDetailsUsingIfsc.endPoint, {
			routeParams: { ifsc },
		});

	getSimulatorAuth = () =>
		this.apiWebClient.get(apiName.getSimulatorAuth.endPoint);

	simulatorLogin = (data) =>
		this.apiTestModeDPoPClient.post(apiName.simulatorLogin.endPoint, { data });

	getApiTestKeys = () =>
		this.dynamicApiWebClient.post(apiName.getApiTestKeys.endPoint);

	getSuccessRateAnalyticsData = (filters) =>
		this.apiWebClient.post(apiName.getSuccessRateAnalyticsData.endPoint, {
			data: filters,
		});

	getSuccessRateLastUpdate = () =>
		this.apiWebClient.get(apiName.getSuccessRateLastUpdate.endPoint);

	getSelfAccountsList = () =>
		this.apiWebClient.get(apiName.getSelfAccountsList.endPoint);

	addNewSelfAccount = (data) =>
		this.apiWebClient.post(apiName.addNewSelfAccount.endPoint, {
			data,
		});

	initOnboarding = (data) =>
		this.apiWebClient.post(apiName.initOnboarding.endPoint, {
			data,
		});

	updateSelfBankStatus = ({ type, accountId }) =>
		this.apiWebClient.put(apiName.updateSelfBankStatus.endPoint, {
			routeParams: { type, accountId },
		});

	getVendorAccountsList = (params) =>
		this.apiWebClient.get(apiName.getVendorAccountsList.endPoint, {
			params,
		});

	getVendorProfileDetails = (vendorId) =>
		this.apiWebClient.get(apiName.getVendorProfileDetails.endPoint, {
			routeParams: { vendorId },
		});

	updateVendorBankStatus = ({ type, vendorId }) =>
		this.apiWebClient.put(apiName.updateVendorBankStatus.endPoint, {
			routeParams: { type, vendorId },
		});

	addNewVendorAccount = (data) =>
		this.apiWebClient.post(apiName.addNewVendorAccount.endPoint, {
			data,
		});

	initVendorOnboarding = (data) =>
		this.apiWebClient.post(apiName.initVendorOnboarding.endPoint, {
			data,
		});

	getVendorJourneyDetails = ({ merchantId, vendorId }) =>
		this.apiWebClient.get(apiName.getVendorJourneyDetails.endPoint, {
			routeParams: { merchantId, vendorId },
		});

	resendEDDLink = (vendorId) =>
		this.apiWebClient.post(apiName.resendEDDLink.endPoint, {
			routeParams: { vendorId },
		});

	getVendorMetaDetails = (metaId) =>
		this.apiWebClient.get(apiName.getVendorMetaDetails.endPoint, {
			routeParams: { metaId },
		});

	initEddWorkflow = (data) =>
		this.apiWebClient.post(apiName.initEddWorkflow.endPoint, { data });

	initiateVendorOtp = (data, captchaValue) =>
		this.apiWebClient.post(apiName.initiateVendorOtp.endPoint, {
			data,
			headers: {
				[Constants.HCAPTCHA_HEADER_KEY]: captchaValue,
			},
		});

	initiateVendorLogin = (data) =>
		this.apiWebClient.post(apiName.initiateVendorLogin.endPoint, {
			data,
		});
}

export default Api;
